import { GoogleAuthProvider, signInWithPopup } from "firebase/auth";
import { ref, set } from "firebase/database";
import { doc, getDoc, setDoc } from "firebase/firestore";
import { AlertCircle } from "lucide-react";
import React, { useEffect, useState } from "react";
import { useUser } from "../contexts/UserContext";
import { auth, db, rtdb } from "../firebaseConfig";

const Auth = () => {
	const { setUser, setEncryptionKey } = useUser();
	const [showError, setShowError] = useState(false);
	const [error, setError] = useState("");
	const [setSessionId] = useState(null);

	useEffect(() => {
		const urlParams = new URLSearchParams(window.location.search);
		const sid = urlParams.get("session_id");
		if (sid) {
			setSessionId(sid);
			localStorage.setItem("pending_session_id", sid);
		}
	}, []);

	const generateEncryptionKey = () => {
		const array = new Uint8Array(32);
		window.crypto.getRandomValues(array);
		return Array.from(array, (byte) =>
			byte.toString(16).padStart(2, "0")
		).join("");
	};

	const signInWithGoogle = async () => {
		try {
			const provider = new GoogleAuthProvider();
			const result = await signInWithPopup(auth, provider);
			const user = result.user;

			const userRef = doc(db, "Users", user.uid);
			const userSnap = await getDoc(userRef);

			if (!userSnap.exists()) {
				console.log("Creating new user document for:", user.uid);
				const encryptionKey = generateEncryptionKey();
				await set(ref(rtdb, `encryption_keys/${user.uid}`), {
					key: encryptionKey,
				});
				await setEncryptionKey(encryptionKey);

				await setDoc(userRef, {
					email: user.email,
					name: user.displayName,
					subscriptionActive: false,
					createdAt: new Date(),
					freeEntriesLeft: 2,
					freeSessionsLeft: 2,
				});
			}

			setUser(user);

			// Handle subscription session
			const pendingSessionId = localStorage.getItem("pending_session_id");
			if (pendingSessionId) {
				localStorage.removeItem("pending_session_id");
				await setDoc(
					userRef,
					{
						stripeSessionId: pendingSessionId,
						subscriptionActive: true,
						subscriptionDate: new Date(),
					},
					{ merge: true }
				);
			}
		} catch (error) {
			console.error("Error signing in with Google:", error);
			setError(error.message);
			setShowError(true);
		}
	};

	return (
		<div className="flex items-center justify-center min-h-screen bg-gray-100 p-4">
			<div className="w-full max-w-md backdrop-blur-lg rounded-3xl shadow-xl overflow-hidden flex flex-col bg-white">
				<div className="w-full text-center p-4">
					<h1 className="text-4xl tracking-wide m-2 sm:m-3">
						<span className="font-normal text-blue-600">
							milburn
						</span>
					</h1>
					{showError && (
						<div className="bg-red-500 text-white p-3 rounded-md mb-4 text-sm font-small flex items-center">
							<AlertCircle size={18} className="mr-2" />
							<span>
								Very sorry! we encountered some errors
								processing the request: {error}
							</span>
						</div>
					)}
					<p className="text-gray-800 text-base">
						Congratulations on taking the first step towards better
						mental health!
					</p>
					<div className="flex justify-center mt-10 mb-5">
						<div className="w-1/2">
							<svg
								xmlns="http://www.w3.org/2000/svg"
								viewBox="0 0 200 250"
								className="w-full bg-white">
								<rect
									x="90"
									y="170"
									width="20"
									height="80"
									fill="#1e3a8a"
								/>
								<polygon
									points="10,170 190,170 100,50"
									fill="#3b82f6"
								/>
								<polygon
									points="20,120 180,120 100,20"
									fill="#60a5fa"
								/>
								<polygon
									points="30,70 170,70 100,0"
									fill="#93c5fd"
								/>
							</svg>
						</div>
					</div>
					<div className="flex justify-center">
						<button
							className="flex items-center justify-center gap-2 p-4 rounded-lg bg-blue-600 text-white cursor-pointer m-4 transition-colors duration-300 sm:px-4 sm:py-4 sm:m-5 text-lg sm:text-base"
							onClick={signInWithGoogle}>
							Continue with Google!
						</button>
					</div>
				</div>
			</div>
		</div>
	);
};

export default Auth;
